import type { Import as UIAPIImport, ImportsSignResponse } from '@customerio/hydra-ui-api';

/**
 * An import: a file (or Google Sheet) uploaded to a workspace and applied to
 * its data. Backed by `/v1/environments/{env}/imports` on the ui_api service
 * (`services/ui_api/imports/`).
 *
 * One record covers two different flows. A CSV import (`people`, `segment`,
 * `event`, `object`, `relationship`) walks the wizard at `/imports`: upload,
 * preprocess, map columns to attributes, validate, review, import. A `geoset`
 * import comes from the geofences page and runs on Temporal instead of the
 * shard backfill, so it has no mapping step. `state` drives both, and the
 * server advances it from a `update_type` on PUT (see `mutations.ts`).
 *
 * Timestamps stay epoch seconds, as on the wire. The six `*_rules` and
 * `*_meta` fields are JSON strings inside the JSON record — `rules.ts` decodes
 * them.
 *
 * The record is a short-lived state machine driven by imperative PUTs and a
 * poll, not a cached collection, so it deliberately does not go through
 * `defineEntity`.
 */
export type ImportRecord = UIAPIImport;

export type ImportState = ImportRecord['state'];

/**
 * Builds a list that has to name every member of `Union` exactly once, so a
 * state the spec gains cannot quietly go unordered — an unordered state has no
 * position, and every comparison against it would silently read as "not
 * reached". Omitting one is a compile error naming the state.
 */
function orderedStates<Union extends string>() {
  return <const T extends readonly Union[]>(
    ...states: [Exclude<Union, T[number]>] extends [never] ? T : [`missing state: ${Exclude<Union, T[number]>}`]
  ) => states;
}

/**
 * Every state the server reports, in the order the pipeline reaches them —
 * the order of `imports.State` on the backend, which is what makes a position
 * in this list comparable.
 *
 * `failed` and `canceled` sit at the end, but neither is a position: an import
 * fails from anywhere, and the server only accepts a cancel at or before
 * `preprocessed`, so a canceled import has never validated a row. They are
 * outcomes rather than progress, which is why {@link gteImportState} answers
 * for neither of them.
 */
export const IMPORT_STATES = orderedStates<ImportState>()(
  'start',
  'preprocessing',
  'preprocessed',
  'validating',
  'validated',
  'importing',
  'imported',
  'failed',
  'canceled',
);

/** The server is working; the record changes without the client asking. */
export const IMPORT_LOADING_STATES: readonly ImportState[] = ['preprocessing', 'validating', 'importing'];

/** Polling stops once the record reaches one of these. */
export const IMPORT_TERMINAL_STATES: readonly ImportState[] = ['imported', 'failed', 'canceled'];

/** What `source` holds for an import made through the public API rather than the app. */
export const IMPORT_API_SOURCE = 'ext_api';

export function isTerminalImportState(state: ImportState | undefined): boolean {
  return state != null && IMPORT_TERMINAL_STATES.includes(state);
}

/**
 * Whether the record has stopped moving.
 *
 * A terminal state is not the end of it for an import: `imported` means the rows were queued, and
 * ingress reports back for a while afterwards. A screen that shows those counts has something
 * left to wait for; one that only reads the state does not.
 */
export function isImportSettled(record: Pick<ImportRecord, 'state' | 'rows_to_import' | 'rows_imported'>): boolean {
  if (!isTerminalImportState(record.state)) {
    return false;
  }
  return record.state !== 'imported' || isImportDone(record);
}

export function isLoadingImportState(state: ImportState): boolean {
  return IMPORT_LOADING_STATES.includes(state);
}

/**
 * Whether a reset can still take a new file for this import. Everything from
 * `importing` onwards has already applied its rows, so the server refuses —
 * except a failed import, which is exactly the case the wizard offers a second
 * file for.
 */
export function isResettableImportState(state: ImportState): boolean {
  return state === 'failed' || IMPORT_STATES.indexOf(state) < IMPORT_STATES.indexOf('importing');
}

/** The states that are a position in the pipeline rather than an outcome. */
export type ImportPipelineState = Exclude<ImportState, 'failed' | 'canceled'>;

/**
 * Whether the import has got at least as far as `threshold`, by position in
 * {@link IMPORT_STATES}. A failed or canceled import has got nowhere: neither
 * is a position, so a wizard asking "is this step behind us" is told no and
 * routes to the step itself.
 */
export function gteImportState(state: ImportState, threshold: ImportPipelineState): boolean {
  if (state === 'failed' || state === 'canceled') {
    return false;
  }
  return IMPORT_STATES.indexOf(state) >= IMPORT_STATES.indexOf(threshold);
}

/**
 * An import stops being downloadable 30 days after it was created; the
 * uploaded file is deleted from the bucket. A Google Sheet import stays
 * available, because the sheet is not ours to expire.
 */
export const IMPORT_EXPIRY_DAYS = 30;

const SECONDS_PER_DAY = 24 * 60 * 60;

export function isImportExpired(record: Pick<ImportRecord, 'created_at'>, nowInSeconds: number): boolean {
  if (record.created_at === 0) {
    return false;
  }
  return nowInSeconds - IMPORT_EXPIRY_DAYS * SECONDS_PER_DAY > record.created_at;
}

export function isImportOriginalAvailable(
  record: Pick<ImportRecord, 'created_at' | 'sheet'>,
  nowInSeconds: number,
): boolean {
  return record.sheet != null || !isImportExpired(record, nowInSeconds);
}

/**
 * `imported` means the rows were queued, not that they landed — ingress reports
 * progress back as `rows_imported`, and this is done once it has accounted for
 * every queued row.
 *
 * The comparison is `>=` rather than `===` because the underlying count can
 * exceed the total when a row's attributes split the ingress message in two
 * (opsbugs#3238). The read path clamps it today, so the two are equivalent on
 * the wire; the looser comparison is what stops a future unclamped read from
 * leaving an import permanently unfinished.
 */
export function isImportDone(record: Pick<ImportRecord, 'state' | 'rows_to_import' | 'rows_imported'>): boolean {
  return record.state === 'imported' && record.rows_imported >= record.rows_to_import;
}

/** Queued rows exist and ingress has started on them, but has not finished. */
export function isImportProcessing(record: Pick<ImportRecord, 'state' | 'rows_to_import' | 'rows_imported'>): boolean {
  const { state, rows_to_import, rows_imported } = record;
  return state === 'imported' && rows_to_import > 0 && rows_imported > 0 && rows_to_import > rows_imported;
}

/** Rows are queued and ingress has not picked any of them up. */
export function isImportQueued(record: Pick<ImportRecord, 'state' | 'rows_to_import' | 'rows_imported'>): boolean {
  return record.state === 'imported' && record.rows_to_import > 0 && record.rows_imported === 0;
}

/** The rows are on their way: still being queued, queued, or being processed. */
export function isImportPending(record: Pick<ImportRecord, 'state' | 'rows_to_import' | 'rows_imported'>): boolean {
  return record.state === 'importing' || isImportQueued(record) || isImportProcessing(record);
}

export const importKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'import'] as const,
  lists: (workspaceId: number) => [...importKeys.all(workspaceId), 'list'] as const,
  list: (workspaceId: number, page: number) => [...importKeys.lists(workspaceId), page] as const,
  details: (workspaceId: number) => [...importKeys.all(workspaceId), 'detail'] as const,
  detail: (workspaceId: number, id: string) => [...importKeys.details(workspaceId), id] as const,
};

/** Bucket signature — the `sign` response has exactly one of `gcs` | `s3`. */
export type ImportSignResponse = ImportsSignResponse;

/**
 * Result of uploading the file to the signed bucket URL. `key` is the object
 * path the `create` call references as `import.path`.
 */
export interface BucketUploadResult {
  key: string;
}
